/**
 * Minimal RFC-4180 CSV parser: handles quoted fields, escaped quotes ("")
 * and both CRLF and LF line endings. Enough for the small operator-supplied
 * files uploaded here; we don't need a streaming parser.
 */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let field = "";
  let row: string[] = [];
  let inQuotes = false;

  // Strip a UTF-8 BOM so the first header/cell doesn't carry a stray char
  const input = text.replace(/^﻿/, "");

  for (let i = 0; i < input.length; i++) {
    const char = input[i];

    if (inQuotes) {
      if (char === '"') {
        if (input[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      row.push(field);
      field = "";
    } else if (char === "\n" || char === "\r") {
      // Swallow the \n of a \r\n pair
      if (char === "\r" && input[i + 1] === "\n") i++;
      row.push(field);
      rows.push(row);
      field = "";
      row = [];
    } else {
      field += char;
    }
  }

  // Trailing field/row with no final newline
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  // Drop fully empty rows
  return rows.filter((r) => r.some((cell) => cell.trim().length > 0));
}

/**
 * Read an uploaded file as text, honouring the encoding it was actually saved
 * in. FileReader#readAsText assumes UTF-8 for everything, which mangles the
 * UTF-16 files Excel writes for "Unicode Text" exports and the legacy Windows
 * codepage it writes for plain "CSV". Decode from the bytes instead so emoji
 * and other non-ASCII characters survive the import.
 */
export async function readTextFile(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());

  // BOM-tagged UTF-16 -- TextDecoder strips the BOM itself
  if (bytes[0] === 0xff && bytes[1] === 0xfe) {
    return new TextDecoder("utf-16le").decode(bytes);
  }
  if (bytes[0] === 0xfe && bytes[1] === 0xff) {
    return new TextDecoder("utf-16be").decode(bytes);
  }

  try {
    // The common case: UTF-8, with or without a BOM (parseCsv strips the BOM)
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    // Not valid UTF-8, so fall back to the codepage Excel saves ANSI CSVs in
    return new TextDecoder("windows-1252").decode(bytes);
  }
}

const HEADER_KEYWORDS = ["subject", "description", "line", "tag"];

/** True when a row looks like a header rather than data. */
export function looksLikeHeaderRow(row: string[]): boolean {
  const first = (row[0] || "").trim().toLowerCase();
  return HEADER_KEYWORDS.some((keyword) => first === keyword || first === `${keyword} line`);
}
